import React, { useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PlusCircle, User, Pencil, Trash, QrCode } from 'lucide-react';
import { toast } from 'react-toastify';
import type { Table } from '../../types/@types/table';
import {
  getTables,
  createTable,
  updateTable,
  deleteTable,
} from '../../services/TableService';

interface TableForm {
  number: number;
  capacity: number;
  status: Table['status'];
}

const schema = yup.object({
  number: yup.number().required().min(1),
  capacity: yup.number().required().min(1),
  status: yup.mixed<Table['status']>().oneOf([
    'Disponible',
    'Ocupada',
    'Reservada',
  ]),
});

const statusLabels: Record<Table['status'], string> = {
  Disponible: 'Disponible',
  Ocupada: 'Ocupada',
  Reservada: 'Reservada',
};

const statusClasses: Record<Table['status'], string> = {
  Disponible: 'bg-green-100 text-green-800',
  Ocupada: 'bg-red-100 text-red-800',
  Reservada: 'bg-yellow-100 text-yellow-800',
};

function TableModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TableForm) => void;
  initialData?: Table | null;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TableForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      number: initialData?.number ?? 0,
      capacity: initialData?.capacity ?? 0,
      status: initialData?.status ?? 'Disponible',
    },
  });

  useEffect(() => {
    reset({
      number: initialData?.number ?? 0,
      capacity: initialData?.capacity ?? 0,
      status: initialData?.status ?? 'Disponible',
    });
  }, [initialData, reset]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? `Editar Mesa #${initialData.number}` : 'Crear Nueva Mesa'}
          </h2>
          <button onClick={onClose} className="text-gray-500">✕</button>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit as any)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium">Número de Mesa</label>
            <input
              type="number"
              {...register('number', { valueAsNumber: true })}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {errors.number && (
              <p className="text-red-600 text-sm">{errors.number.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">Capacidad</label>
            <input
              type="number"
              {...register('capacity', { valueAsNumber: true })}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {errors.capacity && (
              <p className="text-red-600 text-sm">{errors.capacity.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">Estado</label>
            <select
              {...register('status')}
              className="mt-1 w-full border rounded px-3 py-2"
            >
              <option value="Disponible">Disponible</option>
              <option value="Ocupada">Ocupada</option>
              <option value="Reservada">Reservada</option>
            </select>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded"
          >
            {initialData ? 'Guardar Cambios' : 'Crear Mesa'}
          </button>
        </form>
      </div>
    </div>
  );
}

function QrModal({ 
  isOpen, 
  onClose, 
  table 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  table: Table | null 
}) {
  if (!isOpen || !table) return null;

  const url = `${window.location.origin}/#/menu/${table.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(url)}`;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Mesa #${table.number}</title>
            <style>
              body { font-family: sans-serif; text-align: center; padding: 50px; }
              .qr-container { border: 2px solid #ccc; padding: 20px; border-radius: 20px; display: inline-block; }
              h1 { color: #4f46e5; margin-bottom: 20px; }
              p { color: #666; margin-top: 10px; }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <h1>DufiWaiter - Mesa #${table.number}</h1>
              <img src="${qrUrl}" width="300" height="300" />
              <p>Escanea para ver el menú y pedir</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-[32px] p-8 w-full max-w-sm shadow-2xl animate-in zoom-in duration-300">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-black text-gray-900">QR Mesa #{table.number}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">✕</button>
        </div>
        
        <div className="flex flex-col items-center gap-6">
          <div className="p-4 bg-slate-50 rounded-3xl border-2 border-slate-100 shadow-inner">
            <img 
              src={qrUrl} 
              alt={`QR Mesa ${table.number}`} 
              className="w-full h-auto rounded-xl"
            />
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-500 font-medium break-all mb-4 px-4 py-2 bg-slate-100 rounded-lg">
              {url}
            </p>
            
            <div className="flex gap-3 w-full">
              <button
                onClick={handlePrint}
                className="flex-1 bg-indigo-600 text-white py-3 rounded-2xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all active:scale-95"
              >
                Imprimir QR
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(url);
                  toast.success('Link copiado');
                }}
                className="px-6 bg-slate-100 text-slate-600 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-95"
              >
                Copiar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Tables() {
  const queryClient = useQueryClient();
  const { data: tables } = useQuery({ queryKey: ['tables'], queryFn: getTables });

  const createMutation = useMutation({
    mutationFn: createTable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Mesa creada');
    },
    onError: () => toast.error('Error al crear la mesa'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<Table, 'id'> }) =>
      updateTable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Mesa actualizada');
    },
    onError: () => toast.error('Error al actualizar la mesa'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Mesa eliminada');
    },
    onError: () => toast.error('Error al eliminar la mesa'),
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Table | null>(null);
  const [qrModal, setQrModal] = React.useState<Table | null>(null);

  const openNew = () => {
    setEditing(null);
    setIsOpen(true);
  };

  const handleEdit = (table: Table) => {
    setEditing(table);
    setIsOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar mesa?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (data: TableForm) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
    setIsOpen(false);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
           <h1 className="text-3xl font-black text-slate-900 tracking-tight">Gestión de Mesas</h1>
           <p className="text-slate-500 font-medium">Administra tus mesas y códigos QR</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-bold shadow-xl shadow-indigo-100 transition-all hover:bg-indigo-700 active:scale-95"
        >
          <PlusCircle size={20} /> Nueva Mesa
        </button>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {tables?.map((table) => (
          <div
            key={table.id}
            className="group bg-white rounded-[32px] border border-slate-100 shadow-sm p-6 flex flex-col transition-all hover:shadow-xl hover:border-indigo-100 hover:translate-y-[-4px]"
          >
            <div className="flex justify-between items-start mb-4">
               <div className="text-2xl font-black text-slate-900">Mesa #{table.number}</div>
               <button 
                  onClick={() => setQrModal(table)}
                  className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                  title="Ver QR"
               >
                  <QrCode size={18} />
               </button>
            </div>
            
            <div className="text-sm text-slate-400 font-bold flex items-center gap-2 mb-4">
              <User size={16} className="text-indigo-400" /> {table.capacity} Personas
            </div>
            
            <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
               <span
                className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
                  statusClasses[table.status]
                }`}
              >
                {statusLabels[table.status]}
              </span>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(table)}
                  className="p-2 rounded-xl text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => handleDelete(table.id)}
                  className="p-2 rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <TableModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={handleSubmit}
        initialData={editing}
      />
      
      <QrModal 
        isOpen={!!qrModal} 
        onClose={() => setQrModal(null)} 
        table={qrModal} 
      />
    </div>
  );
}
